import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { useRequireAuth } from '@/hooks/use-require-auth';
import { getProReferrals, createPayoutAccount, requestPayout, listPayouts } from '@/lib/pro-api';
import { formatCents } from '@/lib/premium-api';
import {
  ArrowLeft, Copy, Check, Share2, DollarSign, CreditCard,
  CheckCircle, AlertTriangle, UserPlus, Clock, Users
} from 'lucide-react';

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return ''; }
}

function sourceLabel(src) {
  if (src === 'PREMIUM_EVENT') return 'Premium Event';
  if (src === 'PRO_SUBSCRIPTION') return 'PRO subscription';
  if (src === 'PRO_SUBSCRIPTION_RENEWAL') return 'PRO renewal';
  return src;
}

export default function ProReferralsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { ready } = useRequireAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(null);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [connectBusy, setConnectBusy] = useState(false);
  const [payoutBusy, setPayoutBusy] = useState(false);
  const [confirmPayout, setConfirmPayout] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg, tone = 'success') => {
    setToast({ msg, tone });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await getProReferrals();
      setData(r);
      if (r.is_pro && r.payouts?.account_id) {
        try { const h = await listPayouts(null, 20); setPayoutHistory(h.items || []); }
        catch { /* non-critical */ }
      } else {
        setPayoutHistory([]);
      }
    } catch (e) {
      setError(e?.message || 'Could not load referral earnings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (ready) load(); }, [ready, load]);

  const onCopy = async (value, which) => {
    if (!value) return;
    try { await navigator.clipboard.writeText(value); }
    catch { /* fallback: silent */ }
    setCopied(which);
    setTimeout(() => setCopied((c) => (c === which ? null : c)), 1600);
  };

  const onShare = async (link) => {
    if (!link) return;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Join ALLVIEWS', text: 'Host memorable events on ALLVIEWS — every guest contributes, you curate.', url: link });
        return;
      } catch { /* dismissed */ }
    }
    await onCopy(link, 'link');
  };

  const onSetupConnect = async () => {
    setConnectBusy(true);
    try {
      const link = await createPayoutAccount();
      window.open(link.onboarding_url, '_blank');
      setTimeout(() => load(), 800);
    } catch (e) {
      const code = e?.code || '';
      if (code === 'STRIPE_CONNECT_NOT_ENABLED') {
        showToast('Payout onboarding is currently disabled on the platform. We will notify you when it is ready.', 'warn');
      } else {
        showToast(e?.message || 'Could not set up payouts. Please try again.', 'error');
      }
    } finally {
      setConnectBusy(false);
    }
  };

  const onRequestPayout = async () => {
    if (!data?.payouts) return;
    setPayoutBusy(true);
    try {
      const r = await requestPayout();
      showToast(`${formatCents(r.amount_cents, r.currency)} is on its way to your bank.`);
      setConfirmPayout(false);
      await load();
    } catch (e) {
      const code = e?.code || '';
      const msg =
        code === 'BELOW_MIN_PAYOUT' ? 'You need at least $25 in earnings before you can cash out.' :
        code === 'PAYOUTS_NOT_ENABLED' ? 'Finish payout onboarding first.' :
        code === 'PAYOUT_IN_PROGRESS' ? 'Another payout is already processing — hang tight.' :
        e?.message || 'Payout could not be initiated. Please try again.';
      showToast(msg, 'error');
      setConfirmPayout(false);
    } finally {
      setPayoutBusy(false);
    }
  };

  if (!ready) return null;

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 pb-24" data-testid="pro-referrals-screen">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-16 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-lg shadow-lg text-sm font-semibold transition-all ${
          toast.tone === 'error' ? 'bg-red-600 text-white' : toast.tone === 'warn' ? 'bg-amber-500 text-white' : 'bg-emerald-600 text-white'
        }`} data-testid="pro-referrals-toast">{toast.msg}</div>
      )}

      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-[#146BFF] font-semibold text-sm mb-4 hover:underline focus:outline-none focus:ring-2 focus:ring-[#146BFF] rounded" data-testid="pro-referrals-back">
        <ArrowLeft className="w-4 h-4" /> back
      </button>

      {/* Header */}
      <p className="text-[11px] font-extrabold tracking-[1.5px] text-[#146BFF] mb-1">PRO EARNINGS</p>
      <h1 className="text-2xl font-extrabold text-[#0B1B46]" data-testid="pro-referrals-title">Referral earnings</h1>
      <p className="text-sm text-[#5B6472] mt-1">Earn 10% for life on every payment from hosts you refer.</p>

      {loading ? (
        <div className="flex justify-center py-16" data-testid="pro-referrals-loading">
          <div className="w-8 h-8 border-2 border-[#146BFF] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className="bg-white border border-[#E3E8EF] rounded-xl p-5 mt-6" data-testid="pro-referrals-error">
          <p className="text-sm text-[#0B1B46]">{error}</p>
          <button onClick={load} className="mt-3 h-10 px-5 bg-[#146BFF] text-white rounded-lg font-semibold text-sm hover:bg-[#0F55CC] transition-colors focus:outline-none focus:ring-2 focus:ring-[#146BFF]" data-testid="pro-referrals-retry-btn">Retry</button>
        </div>
      ) : data && !data.is_pro ? (
        <UpgradeNudge navigate={navigate} />
      ) : data ? (
        <>
          <ShareCard data={data} copied={copied} onCopy={onCopy} onShare={onShare} />
          <StatsGrid summary={data.summary} />
          <ConnectAndPayoutSection
            payouts={data.payouts}
            connectBusy={connectBusy}
            payoutBusy={payoutBusy}
            confirmPayout={confirmPayout}
            setConfirmPayout={setConfirmPayout}
            onSetupConnect={onSetupConnect}
            onRequestPayout={onRequestPayout}
          />
          <HowItWorks />
          <RecentActivity recent={data.recent} />
          {data.payouts?.account_id ? <PayoutHistorySection rows={payoutHistory} /> : null}
          <p className="text-xs text-[#5B6472] text-center mt-6 leading-relaxed">
            Self-referrals are silently ignored. Commissions settle only after Stripe confirms the payment;
            refunds automatically reverse the row.
          </p>
        </>
      ) : null}

      {/* Payout confirmation dialog */}
      {confirmPayout && data?.payouts && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="pro-referrals-payout-confirm-overlay">
          <div className="bg-white rounded-xl p-6 shadow-xl max-w-sm mx-4 w-full" data-testid="pro-referrals-payout-confirm-dialog">
            <h3 className="text-lg font-extrabold text-[#0B1B46] mb-2">Request payout</h3>
            <p className="text-sm text-[#5B6472] mb-5">
              Send {formatCents(data.payouts.available_balance_cents, data.payouts.currency)} to your connected bank account?
            </p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmPayout(false)} disabled={payoutBusy} className="flex-1 h-10 border border-[#E3E8EF] text-[#0B1B46] rounded-lg font-semibold text-sm hover:bg-[#F3F5F8] transition-colors focus:outline-none focus:ring-2 focus:ring-[#146BFF] disabled:opacity-50" data-testid="pro-referrals-payout-cancel-btn">Cancel</button>
              <button onClick={onRequestPayout} disabled={payoutBusy} className="flex-1 h-10 bg-[#146BFF] text-white rounded-lg font-semibold text-sm hover:bg-[#0F55CC] transition-colors focus:outline-none focus:ring-2 focus:ring-[#146BFF] disabled:opacity-50 flex items-center justify-center gap-2" data-testid="pro-referrals-payout-send-btn">
                {payoutBusy && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Upgrade nudge (non-PRO) ---------- */
function UpgradeNudge({ navigate }) {
  return (
    <div className="bg-[#F4F8FF] border border-[#D0DFFF] rounded-xl p-5 mt-6" data-testid="pro-referrals-upgrade-nudge">
      <h2 className="text-lg font-extrabold text-[#0B1B46] mb-1">Unlock the referral program</h2>
      <p className="text-sm text-[#5B6472] leading-relaxed">
        ALLVIEWS PRO subscribers receive a permanent referral code and earn 10% for life on every
        payment from hosts they refer &mdash; Premium Event purchases AND PRO renewals.
      </p>
      <button onClick={() => navigate('/host-premium')} className="mt-4 h-10 px-6 bg-[#146BFF] text-white rounded-lg font-semibold text-sm hover:bg-[#0F55CC] transition-colors focus:outline-none focus:ring-2 focus:ring-[#146BFF]" data-testid="pro-referrals-upgrade-btn">See PRO pricing</button>
    </div>
  );
}

/* ---------- Share card ---------- */
function ShareCard({ data, copied, onCopy, onShare }) {
  return (
    <div className="bg-[#F4F8FF] border-2 border-[#146BFF] rounded-xl p-5 mt-6" data-testid="pro-referrals-share-card">
      <p className="text-[11px] font-extrabold text-[#5B6472] uppercase tracking-wide mb-1">Your permanent referral code</p>
      <div className="flex items-start gap-3 mb-3">
        <div className="flex-1">
          <p className="text-2xl font-extrabold text-[#0B1B46] tracking-[3px] font-mono" data-testid="pro-referrals-code">{data.referral_code || '\u2014'}</p>
          <button onClick={() => onCopy(data.referral_code, 'code')} className="flex items-center gap-1.5 mt-1.5 text-sm font-bold text-[#146BFF] hover:underline focus:outline-none focus:ring-2 focus:ring-[#146BFF] rounded" data-testid="pro-referrals-copy-code-btn">
            {copied === 'code' ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
            <span className={copied === 'code' ? 'text-emerald-600' : ''}>{copied === 'code' ? 'Copied' : 'Copy code'}</span>
          </button>
        </div>
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#EFF5FF] text-[#146BFF] text-xs font-bold" data-testid="pro-referrals-rate">
          &#9733; {data.summary?.commission_rate_pct || 10}% for life
        </span>
      </div>

      <div className="h-px bg-[#E3E8EF] my-4" />

      <p className="text-[11px] font-extrabold text-[#5B6472] uppercase tracking-wide mb-1">Share link</p>
      <p className="text-[13px] text-[#0B1B46] font-mono leading-5 break-all mb-3" data-testid="pro-referrals-link">{data.share_link || '\u2014'}</p>
      <div className="flex items-center gap-3">
        <button onClick={() => onShare(data.share_link)} className="h-10 px-5 bg-[#146BFF] text-white rounded-lg font-semibold text-sm hover:bg-[#0F55CC] transition-colors focus:outline-none focus:ring-2 focus:ring-[#146BFF]" data-testid="pro-referrals-share-btn">Share</button>
        <button onClick={() => onCopy(data.share_link, 'link')} className="flex items-center gap-1.5 h-10 px-4 border border-[#146BFF] text-[#146BFF] bg-white rounded-lg font-bold text-sm hover:bg-[#F4F8FF] transition-colors focus:outline-none focus:ring-2 focus:ring-[#146BFF]" data-testid="pro-referrals-copy-link-btn">
          {copied === 'link' ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
          <span className={copied === 'link' ? 'text-emerald-600' : ''}>{copied === 'link' ? 'Link copied' : 'Copy link'}</span>
        </button>
      </div>
    </div>
  );
}

/* ---------- Stats grid ---------- */
function StatsGrid({ summary }) {
  const s = summary || {};
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
      <StatTile label="Lifetime earned" value={formatCents(s.lifetime_earned_cents || 0, 'usd')} testId="pro-referrals-stat-earned" />
      <StatTile label="Pending" value={formatCents(s.pending_cents || 0, 'usd')} testId="pro-referrals-stat-pending" />
      <StatTile label="Referred hosts" value={s.referred_user_count || 0} testId="pro-referrals-stat-referred" />
      <StatTile label="Reversed" value={formatCents(s.reversed_cents || 0, 'usd')} muted testId="pro-referrals-stat-reversed" />
    </div>
  );
}

function StatTile({ label, value, muted, testId }) {
  return (
    <div className="bg-white border border-[#E3E8EF] rounded-xl p-4" data-testid={testId}>
      <p className="text-[11px] font-bold text-[#5B6472] uppercase tracking-wide mb-1">{label}</p>
      <p className={`text-xl font-extrabold ${muted ? 'text-[#5B6472]' : 'text-[#0B1B46]'}`}>{value}</p>
    </div>
  );
}

/* ---------- Connect & Payout Section (Phase 17) ---------- */
function ConnectAndPayoutSection({ payouts, connectBusy, payoutBusy, confirmPayout, setConfirmPayout, onSetupConnect, onRequestPayout }) {
  if (!payouts) return null;

  const status = payouts.connect_status;
  const isEnabled = payouts.payouts_enabled;
  const available = payouts.available_balance_cents;
  const min = payouts.min_payout_cents;
  const meterPct = Math.min(1, available / Math.max(1, min));
  const platformDown = status === 'platform_not_configured' || status === 'STRIPE_CONNECT_NOT_ENABLED' || status === 'error';

  const connectCopy = isEnabled
    ? { title: 'Payouts enabled', body: 'Your bank details are verified — you can cash out any time you are above the $25 minimum.', Icon: CheckCircle, tone: 'success' }
    : status === 'restricted'
    ? { title: 'Extra info needed', body: 'Stripe needs a bit more info before you can receive payouts. Reopen onboarding to finish.', Icon: AlertTriangle, tone: 'warn' }
    : status === 'onboarding' || status === 'pending'
    ? { title: 'Finish payout onboarding', body: 'You are almost there — complete the Stripe onboarding to unlock cash payouts.', Icon: UserPlus, tone: 'info' }
    : platformDown
    ? { title: 'Payouts coming soon', body: 'The platform is finishing Stripe Connect setup. We will notify you as soon as payouts open.', Icon: Clock, tone: 'neutral' }
    : { title: 'Set up payouts', body: 'Connect your bank in a couple of minutes to cash out your referral earnings.', Icon: CreditCard, tone: 'info' };

  const toneClasses = {
    success: 'bg-emerald-50 border-emerald-200',
    warn: 'bg-amber-50 border-amber-200',
    info: 'bg-[#F4F8FF] border-[#D0DFFF]',
    neutral: 'bg-[#F3F5F8] border-[#E3E8EF]',
  };

  return (
    <>
      {/* Connect status card */}
      <div className={`rounded-xl border p-5 mt-6 ${toneClasses[connectCopy.tone] || toneClasses.neutral}`} data-testid="pro-referrals-connect-card">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-[#EDF3FF] flex items-center justify-center shrink-0">
            <connectCopy.Icon className={`w-5 h-5 ${isEnabled ? 'text-emerald-600' : 'text-[#146BFF]'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-extrabold text-[#0B1B46]" data-testid="pro-referrals-connect-title">{connectCopy.title}</h3>
            <p className="text-sm text-[#5B6472] mt-0.5">{connectCopy.body}</p>
          </div>
          {isEnabled && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold shrink-0" data-testid="pro-referrals-connect-status">
              &#10003; READY
            </span>
          )}
        </div>
        {!isEnabled && !platformDown && (
          <button onClick={onSetupConnect} disabled={connectBusy} className="mt-4 w-full h-10 bg-[#146BFF] text-white rounded-lg font-semibold text-sm hover:bg-[#0F55CC] transition-colors disabled:opacity-50 flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-[#146BFF]" data-testid="pro-referrals-connect-setup-btn">
            {connectBusy && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
            {status === 'onboarding' || status === 'not_started' ? 'Set up payouts' : 'Finish onboarding'}
          </button>
        )}
      </div>

      {/* Available balance + Request payout */}
      <div className="bg-white border border-[#E3E8EF] rounded-xl p-5 mt-4" data-testid="pro-referrals-payout-card">
        <p className="text-[11px] font-extrabold text-[#5B6472] uppercase tracking-wide mb-1">Available to cash out</p>
        <div className="flex items-end justify-between mb-2">
          <p className="text-3xl font-extrabold text-[#0B1B46] tracking-tight" data-testid="pro-referrals-available-balance">{formatCents(available, payouts.currency)}</p>
          <p className="text-[11px] font-bold text-[#5B6472] uppercase tracking-wide mb-1.5">min ${(min / 100).toFixed(0)}</p>
        </div>
        {/* Threshold meter */}
        <div className="h-2 rounded-full bg-[#EDF1F7] overflow-hidden mb-1">
          <div
            className={`h-2 rounded-full transition-all duration-500 ${meterPct >= 1 ? 'bg-emerald-500' : 'bg-[#146BFF]'}`}
            style={{ width: `${Math.round(meterPct * 100)}%` }}
            data-testid="pro-referrals-threshold-meter-fill"
          />
        </div>
        <p className="text-xs text-[#5B6472] mb-3">
          {available >= min
            ? 'You are above the $25 threshold — cash out any time.'
            : `You need ${formatCents(min - available, payouts.currency)} more to unlock cash out.`}
        </p>
        <button
          onClick={() => setConfirmPayout(true)}
          disabled={!payouts.can_request_payout || payoutBusy}
          className="w-full h-10 bg-[#146BFF] text-white rounded-lg font-semibold text-sm hover:bg-[#0F55CC] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-[#146BFF]"
          data-testid="pro-referrals-request-payout-btn"
        >
          {payoutBusy ? 'Sending payout\u2026' : payouts.in_flight_payout ? 'Payout in progress' : 'Request payout'}
        </button>
        {!payouts.can_request_payout && !payoutBusy && (
          <p className="text-xs text-[#5B6472] text-center mt-2" data-testid="pro-referrals-request-payout-disabled-reason">
            {available < min ? `Earn at least ${formatCents(min, payouts.currency)} first`
              : payouts.in_flight_payout ? 'Another payout is currently processing — check back in a few minutes'
              : !isEnabled ? 'Finish payout onboarding to enable cash out'
              : ''}
          </p>
        )}
        {payouts.lifetime_paid_cents > 0 && (
          <p className="text-xs font-semibold text-[#5B6472] text-center mt-3" data-testid="pro-referrals-lifetime-paid">
            Lifetime paid out: {formatCents(payouts.lifetime_paid_cents, payouts.currency)}
          </p>
        )}
      </div>
    </>
  );
}

/* ---------- How it works ---------- */
function HowItWorks() {
  const steps = [
    { Icon: Share2, text: 'Share your link. Every new host who signs up through it is tied to you forever.' },
    { Icon: DollarSign, text: 'Earn 10% of every Premium Event they purchase and every PRO renewal they pay — for life.' },
    { Icon: CreditCard, text: 'Cash out to your bank via Stripe Connect once you cross the $25 threshold — one tap, automatic transfer.' },
  ];
  return (
    <div className="bg-white border border-[#E3E8EF] rounded-xl p-5 mt-4" data-testid="pro-referrals-how-card">
      <h3 className="text-base font-extrabold text-[#0B1B46] mb-3">How it works</h3>
      {steps.map((s, i) => (
        <div key={i} className="flex items-start gap-2.5 mt-2 first:mt-0">
          <s.Icon className="w-4 h-4 text-[#146BFF] mt-0.5 shrink-0" />
          <p className="text-sm text-[#0B1B46] leading-relaxed">{s.text}</p>
        </div>
      ))}
    </div>
  );
}

/* ---------- Recent commissions ---------- */
function RecentActivity({ recent }) {
  return (
    <div className="mt-6">
      <p className="text-[11px] font-extrabold text-[#0B1B46] uppercase tracking-wide mb-3">Recent activity</p>
      {(!recent || recent.length === 0) ? (
        <div className="bg-[#F4F8FF] border border-[#D0DFFF] rounded-xl p-5" data-testid="pro-referrals-empty">
          <p className="text-sm text-[#5B6472]">No commissions yet. Share your link and the first referred host&apos;s purchase will land here.</p>
        </div>
      ) : (
        recent.map((row) => (
          <div key={row.id} className="bg-white border border-[#E3E8EF] rounded-xl p-4 mt-3 first:mt-0" data-testid={`pro-referrals-row-${row.id}`}>
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-extrabold text-[#0B1B46]">{sourceLabel(row.source)}</p>
                <p className="text-xs text-[#5B6472] mt-0.5">Base {formatCents(row.base_amount_cents, row.currency)} &middot; {fmtDate(row.created_at)}</p>
              </div>
              <div className="text-right shrink-0">
                <p className={`text-base font-extrabold mb-1 ${row.status === 'REVERSED' ? 'text-[#5B6472] line-through' : 'text-emerald-600'}`}>
                  + {formatCents(row.commission_cents, row.currency)}
                </p>
                <StatusPill status={row.status} testId={`pro-referrals-row-status-${row.id}`} />
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

/* ---------- Payout history (Phase 17) ---------- */
function PayoutHistorySection({ rows }) {
  return (
    <div className="mt-6">
      <p className="text-[11px] font-extrabold text-[#0B1B46] uppercase tracking-wide mb-3">Payout history</p>
      {(!rows || rows.length === 0) ? (
        <div className="bg-[#F4F8FF] border border-[#D0DFFF] rounded-xl p-5" data-testid="pro-referrals-payouts-empty">
          <p className="text-sm text-[#5B6472]">No payouts yet. When you cash out, the transfer and its status will show up here.</p>
        </div>
      ) : (
        rows.map((row) => {
          const label = row.status === 'PAID' ? 'Paid to bank' : row.status === 'PENDING' ? 'In transit' : `Payout ${row.status.toLowerCase()}`;
          return (
            <div key={row.id} className="bg-white border border-[#E3E8EF] rounded-xl p-4 mt-3 first:mt-0" data-testid={`pro-referrals-payout-row-${row.id}`}>
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-extrabold text-[#0B1B46]">{label}</p>
                  <p className="text-xs text-[#5B6472] mt-0.5">
                    {fmtDate(row.created_at)}
                    {row.stripe_transfer_id ? ` \u00b7 ${row.stripe_transfer_id}` : ''}
                  </p>
                  {row.error_message && (
                    <p className="text-xs text-[#5B6472] mt-1" data-testid={`pro-referrals-payout-error-${row.id}`}>{row.error_message}</p>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <p className={`text-base font-extrabold mb-1 ${row.status === 'FAILED' ? 'text-[#5B6472] line-through' : 'text-emerald-600'}`}>
                    {formatCents(row.amount_cents, row.currency)}
                  </p>
                  <PayoutStatusPill status={row.status} testId={`pro-referrals-payout-status-${row.id}`} />
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

/* ---------- Status pills ---------- */
function StatusPill({ status, testId }) {
  const cls = status === 'EARNED' ? 'bg-emerald-100 text-emerald-700'
    : status === 'PENDING' ? 'bg-amber-100 text-amber-700'
    : 'bg-gray-100 text-gray-500';
  const icon = status === 'EARNED' ? '\u2713' : status === 'PENDING' ? '\u2026' : '\u21BA';
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${cls}`} data-testid={testId}>{icon} {status}</span>;
}

function PayoutStatusPill({ status, testId }) {
  const cls = status === 'PAID' ? 'bg-emerald-100 text-emerald-700'
    : status === 'PENDING' ? 'bg-amber-100 text-amber-700'
    : 'bg-gray-100 text-gray-500';
  const icon = status === 'PAID' ? '\u2713' : status === 'PENDING' ? '\u2026' : '!';
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${cls}`} data-testid={testId}>{icon} {status}</span>;
}
